import {LipoDetailModel} from "../../../shared/models/lipo-detail.model";
import {asyncScheduler, lastValueFrom, Observable, scheduled, toArray} from "rxjs";
import {LipoFormGroupModel} from "../../../shared/components/models/lipo-form-group.model";
import {
  LipoFormControlIconSetting,
  LipoFormControlModel,
  LipoFormNumeric,
  LipoFormPassword,
  LipoFormSlideToggle,
  LipoFormTextbox
} from "../../../shared/components/models/lipo-form-control.model";
import {ScanAndAttachConfiguration} from "../../../shared/services/models/configuration.model";
import {FormControl, Validators} from "@angular/forms";
import {ScanAndAttachModel} from "../models/scan-and-attach.model";
import {RegexPatterns} from "../../../utils/regex-patterns";

export async function setScanAndAttachSettings(
  model: ScanAndAttachModel,
  settings: ScanAndAttachConfiguration,
  detailModel: LipoDetailModel
): Promise<void> {
  const formGroups = await lastValueFrom(getScanAndAttachFields(model, settings).pipe(toArray()));

  if (detailModel) {
    formGroups.forEach(detailModel.form!.addGroup, detailModel.form);
  }
}

export function getScanAndAttachFields(scanAndAttachModel: ScanAndAttachModel, settings: ScanAndAttachConfiguration): Observable<LipoFormGroupModel> {
  const mailFields = createMailFields(scanAndAttachModel);
  const folderFields = createFoldersFields(scanAndAttachModel);

  return scheduled([
    new LipoFormGroupModel({
      key: `${settings.formKey}_mail`,
      label: 'scanAndAttachMailFields',
      order: 1,
      controls: mailFields,
      expansionActive: true,
      expansion: {background: true, hideToggle: true, expanded: true, disabled: true},
    }),
    new LipoFormGroupModel({
      key: `${settings.formKey}_folder`,
      label: 'scanAndAttachFolderFields',
      order: 2,
      controls: folderFields,
      expansionActive: true,
      expansion: {background: true, hideToggle: true, expanded: true, disabled: true},
    }),
  ], asyncScheduler);
}

function createMailFields(scanAndAttachModel: ScanAndAttachModel): LipoFormControlModel[] {
  const passwordValue =  new FormControl(scanAndAttachModel.MailPassword, Validators.required)
  passwordValue.valueChanges.subscribe(value => {
    if (value && !value.startsWith('encrypt_') && !value.endsWith('==')) {
      passwordValue.setValue('encrypt_' + value)
    }
  })

  return [
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.Mailserver, Validators.required),
      key: 'Mailserver',
      label: 'Mailserver',
      hint: 'hint.mailserver',
    }),
    new LipoFormNumeric({
      value: new FormControl(scanAndAttachModel.Port, Validators.required),
      key: 'Port',
      label: 'Port',
      hint: 'hint.port',
      min: 25,
      max: 993
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.SenderAddress, [Validators.required, Validators.pattern(RegexPatterns.Email)]),
      key: 'SenderAddress',
      label: 'SenderAddress',
      hint: 'hint.senderAddress',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.MailUsername, Validators.required),
      key: 'MailUsername',
      label: 'MailUsername',
      hint: 'hint.mailUsername',
    }),
    new LipoFormPassword({
      value: passwordValue,
      key: 'MailPassword',
      label: 'MailPassword',
      hint: 'hint.mailPassword',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.ErrorMails, [Validators.required, Validators.pattern(RegexPatterns.EmailScanAndAttach)]),
      key: 'ErrorMails',
      label: 'ErrorMails',
      placeholder: 'mail1@example.com;mail2@example.com',
      hint: 'hint.errorMails',
    }),
    new LipoFormSlideToggle({
      value: new FormControl(scanAndAttachModel.EnableSSL),
      key: 'EnableSSL',
      label: 'EnableSSL',
      settings: {
        endIcon: {
          tooltip: 'tooltip.enableSSL',
        } as LipoFormControlIconSetting
      }
    }),
    new LipoFormSlideToggle({
      value: new FormControl(scanAndAttachModel.TestConnection),
      key: 'TestConnection',
      label: 'TestConnection',
      settings: {
        endIcon: {
          tooltip: 'tooltip.testConnection',
        } as LipoFormControlIconSetting
      }
    }),
    new LipoFormSlideToggle({
      value: new FormControl(scanAndAttachModel.SendMail),
      key: 'SendMail',
      label: 'SendMail',
      settings: {
        endIcon: {
          tooltip: 'tooltip.sendMail',
        } as LipoFormControlIconSetting
      }
    }),
  ]
}

function createFoldersFields(scanAndAttachModel: ScanAndAttachModel): LipoFormControlModel[] {
  return [
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.SourceFolder, Validators.required),
      key: 'SourceFolder',
      label: 'SourceFolder',
      hint: 'hint.sourceFolder',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.ManualSourceFolder, Validators.required),
      key: 'ManualSourceFolder',
      label: 'ManualSourceFolder',
      hint: 'hint.manualSourceFolder',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.InProgressFolder, Validators.required),
      key: 'InProgressFolder',
      label: 'InProgressFolder',
      hint: 'hint.inProgressFolder',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.TargetFolder, Validators.required),
      key: 'TargetFolder',
      label: 'TargetFolder',
      hint: 'hint.targetFolder',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.ErrorFolder, Validators.required),
      key: 'ErrorFolder',
      label: 'ErrorFolder',
      hint: 'hint.errorFolder',
    }),
    new LipoFormTextbox({
      value: new FormControl(scanAndAttachModel.ArchiveFolder, Validators.required),
      key: 'ArchiveFolder',
      label: 'ArchiveFolder',
      hint: 'hint.archiveFolder',
    })
  ]
}
